"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import ServiceWorkerRegistration from "@/app/components/ServiceWorkerRegistration";

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    // eye-off
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="opacity-80"
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.81 21.81 0 0 1 5.06-6.94" />
      <path d="M1 1l22 22" />
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.74 21.74 0 0 1-3.18 4.56" />
      <path d="M14.12 14.12a3 3 0 0 1-4.24-4.24" />
      <path d="M9.88 9.88 9 9" />
    </svg>
  ) : (
    // eye
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="opacity-80"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function RestaurantLoginPage() {
  const router = useRouter();
  const supabase = createClient();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
  e.preventDefault();
  setError(null);
  setLoading(true);

  try {
    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: username.trim(),
      password,
    });

    if (error) {
      setError(error.message);
      return;
    }

    // Look up which restaurant this user belongs to
    const { data: restaurantUser } = await supabase
      .from("restaurant_users")
      .select("restaurant_id")
      .eq("user_id", authData.user.id)
      .single();

    if (!restaurantUser) {
      setError("No restaurant found for this account.");
      await supabase.auth.signOut({ scope: "local" });
      return;
    }

    // Look up the restaurant slug
    const { data: restaurant } = await supabase
      .from("restaurants")
      .select("slug")
      .eq("id", restaurantUser.restaurant_id)
      .single();

    if (!restaurant?.slug) {
      router.push("/restaurant");
    } else {
      router.push("/restaurant/dining");
    }

    router.refresh();
  } catch {
    setError("Something went wrong. Try again.");
  } finally {
    setLoading(false);
  }
}

  return (
    <main className="min-h-screen bg-bg-base text-text-primary">
      <div className="mx-auto max-w-md px-6 py-24">
        <h1 className="text-2xl font-semibold">Restaurant Login</h1>
        <p className="mt-2 text-sm text-text-primary/60">
          Sign in to your in-house service view.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <div>
            <label className="text-sm text-text-primary/70">Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-text-primary outline-none focus:border-white/30"
              placeholder="Username"
            />
          </div>

          <div>
            <label className="text-sm text-text-primary/70">Password</label>

            <div className="relative mt-2">
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 pr-12 text-text-primary outline-none focus:border-white/30"
                placeholder="Password"
              />

              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-2 py-2 text-text-primary/80 hover:bg-white/10"
              >
                <EyeIcon open={showPw} />
              </button>
            </div>
          </div>

          {error && (
            <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !username.trim() || !password}
            className={[
              "mt-2 inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-base font-semibold transition",
              loading || !username.trim() || !password
                ? "bg-white/15 text-text-primary/40"
                : "bg-white text-black hover:bg-white/90",
            ].join(" ")}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
      {/* The installed PWA starts here (manifest start_url), so register the staff worker */}
      <ServiceWorkerRegistration />
    </main>
  );
}